/**
 * MapPreview — web implementation with Mapbox GL JS v3.
 *
 * Uses DOM Markers (react-map-gl's <Marker> component) for vehicle pins.
 * Native uses map-engine symbols; web keeps real, keyboard-accessible buttons.
 *
 * Same Mapbox Standard basemap and camera as the native apps: the style URL,
 * the Standard configuration and the discovery pitch/bearing/zoom all come from
 * `lib/maps/mapbox-standard`, so the two platforms cannot drift apart.
 */
import React, { useMemo, useRef, useCallback, useEffect, useState, type ReactNode } from 'react'
import { View, Text } from 'react-native'
import { LinearGradient } from 'expo-linear-gradient'
import 'mapbox-gl/dist/mapbox-gl.css'
import Map, { Marker, NavigationControl, type MapRef } from 'react-map-gl/mapbox'
import { Map as MapIcon, Minus, Plus } from '@/components/ui/icons'
import { useTranslation } from 'react-i18next'
import { Pressable } from '../ui/Pressable'
import { Button } from '../ui/Button'
import { GlassSurface } from '../ui/GlassSurface'
import { useSemanticColors, RADIUS, DURATION } from '../../lib/theme/tokens'
import { useTheme } from '../../lib/hooks/use-theme'
import { COLORS, FONTS, SPACING } from '../../lib/brand'
import { webShadow } from '../../lib/theme/web-shadow'
import { getVehicleDisplayPriceCents } from '../../lib/utils/pricing'
import { formatPrice } from '../../lib/utils/format'
import { usePressAction } from '../../lib/hooks/use-press-action'
import { WEB_MAP_SCROLL_ZOOM } from '../../lib/maps/web-map-gestures'
import { useLockPageScrollOverMap } from '../../lib/maps/use-web-map-wheel-lock'
import {
  applyMapboxWebConfig,
  ensureMapboxWeb,
  hasMapboxWebToken,
  MAPBOX_WEB_NETWORK_ERROR,
  MAPBOX_WEB_STYLE_URL,
  MAPBOX_WEB_TOKEN,
} from '../../lib/maps/mapbox-web'
import {
  MAPBOX_DEFAULT_SV_DEPARTMENT_FRAME,
  MAPBOX_DISCOVERY_BEARING,
  MAPBOX_DISCOVERY_HERO_ZOOM,
  MAPBOX_DISCOVERY_PITCH,
  type MapInitialFrame,
  type MapboxCameraBounds,
} from '../../lib/maps/mapbox-standard'
import type { NearbyVehicle, UserLocation } from '../../lib/types/vehicle.types'
import { MapClusterSheet } from './MapClusterSheet'
import {
  buildVehicleMapClusterIndex,
  countLocatedVehicles,
  normalizeMapClusterZoom,
  type VehicleMapCluster,
  type VehicleMapMarker,
} from './map-marker-layout'

ensureMapboxWeb()

const DEFAULT_CENTER = { lng: -89.2182, lat: 13.6929 } // San Salvador
const MAP_NETWORK_ERROR = MAPBOX_WEB_NETWORK_ERROR
const DEFAULT_MOBILE_MAP_PREVIEW_HEIGHT = 240

/** Count/brand and map action share one top row, leaving the coast unobscured. */
const MAP_OVERLAY_CHIP_HEIGHT = 44
const MAP_OVERLAY_INSET = 12

/**
 * Explicit zoom controls for the compact preview, matching the native card.
 * One step per tap, clamped to the same range the native map uses so the two
 * platforms cannot drift apart. Short ease — a long cinematic move under a
 * finger that is still touching the control reads as lag.
 */
const MAP_ZOOM_STEP = 1
const MAP_ZOOM_MIN = 6
const MAP_ZOOM_MAX = 18
const MAP_ZOOM_ANIMATION_MS = 240

/**
 * Where a control floating over the map sits, matched to the back button the
 * full-screen map route puts in the opposite corner.
 *
 * That button is a 44pt disc centred in a 52pt target inside a container at
 * `SPACING.lg`, so its visible edge lands at `SPACING.lg + SPACING.xs`.
 * mapbox ships its own controls at a flat 10px, which put the zoom stack
 * roughly three times closer to the corner than the back arrow it reads as a
 * pair with.
 */
const MAP_FLOATING_CONTROL_INSET = SPACING.lg + SPACING.xs

/** Reserve a marker's full 44px touch target in the camera framing. */
const MAP_PILL_HEIGHT = 44
/** Clear air a pill keeps around itself, so two of them read as two. */
const MAP_PILL_GAP = 8

/**
 * A pill is opaque and a map label is not, so wherever the two meet the pill
 * has to win cleanly. The white keyline does that on the dark style but merges
 * into the light halo the light style draws behind its own labels, which is how
 * "17 carros" ended up looking welded to "San Salvador". Every pill therefore
 * carries a hairline of shadow *outside* its keyline: fill → keyline →
 * separator → map, in both themes and over any label.
 */
const MAP_PILL_SHADOW = '0 1px 4px rgba(0,0,0,0.16)'

/**
 * Ceiling for a pill's z-index.
 *
 * When labels overlap, denser groups take visual priority. The cap stays below the 10
 * the floating chips and the CTA use: mapbox's container is
 * `position: relative` with no z-index of its own, so it opens no stacking
 * context and a marker asking for 17 would paint over the "Ver mapa" button.
 */
const MAP_PILL_MAX_Z_INDEX = 9

type RenderedVehicleMapMarker = VehicleMapMarker & {
  label: string
  accessibilityLabel: string
}

interface MapPreviewProps {
  vehicles: NearbyVehicle[]
  userLocation: UserLocation | null
  onViewMap: () => void
  onVehiclePress?: (vehicleId: string) => void
  desktop?: boolean
  height?: number
  /**
   * When true, the "X cerca de vos" overlay badge is suppressed. The
   * full-screen map screen passes this because it has its own
   * floating BackButton at top-left and the badge would render
   * underneath the back arrow (the bug Carlos hit on web).
   */
  hideCountBadge?: boolean
  compactPreview?: boolean
  /** Initial compact-map framing; it does not filter the inventory. */
  initialFrame?: MapInitialFrame
  fitRequest?: number
  /**
   * Controls owned by the full-screen route. Keeping them in this map stack
   * makes their paint order match native, rather than leaving a web-only map
   * with no way back.
   */
  mapChrome?: ReactNode
}

// ---- Price Pin ----

function stopMarkerDomEvent(event: React.SyntheticEvent<HTMLElement>) {
  event.stopPropagation()
  const nativeEvent = event.nativeEvent as Event & {
    stopImmediatePropagation?: () => void
  }
  nativeEvent.stopImmediatePropagation?.()
}

function handleMarkerKeyPress(
  event: React.KeyboardEvent<HTMLButtonElement>,
  onActivate: () => void,
) {
  if (event.key !== 'Enter' && event.key !== ' ') return
  event.preventDefault()
  stopMarkerDomEvent(event)
  onActivate()
}

function MapMarkerInteractionStyles({
  colors,
  isDark,
}: {
  colors: ReturnType<typeof useSemanticColors>
  isDark: boolean
}) {
  return (
    <style>{`
      .tago-map-pin-button {
        appearance: none;
        -webkit-appearance: none;
        background: transparent;
        border: 0;
        color: inherit;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        font: inherit;
        margin: -8px -8px 0;
        min-height: 44px;
        min-width: 44px;
        padding: 8px 8px 0;
        pointer-events: auto;
        touch-action: manipulation;
        user-select: none;
      }
      @media (prefers-reduced-motion: no-preference) {
        .tago-map-pin-button {
          transition: transform ${DURATION.fast}ms ease, filter ${DURATION.fast}ms ease;
        }
      }
      .tago-map-pin-button:hover {
        transform: scale(1.06);
      }
      .tago-map-pin-button:active {
        transform: scale(0.98);
      }
      .tago-map-pin-button:focus-visible {
        outline: none;
      }
      .tago-map-pin-button:focus-visible .tago-map-price-pill,
      .tago-map-pin-button:focus-visible .tago-map-cluster-pill {
        box-shadow: 0 0 0 3px rgba(239, 78, 74, 0.24), ${MAP_PILL_SHADOW};
      }
      /* mapbox ships its zoom buttons at 29x29 on a hardcoded white card.
         They are the only controls on the full-screen map: at 29px they are
         the hardest thing on the screen to hit, and left white they were the
         only unthemed surface on a near-black map, 50pt from a BackButton
         that had adapted. Size, surface, divider and glyph are all set here so
         the two floating controls follow one rule. */
      .mapboxgl-ctrl-top-right .mapboxgl-ctrl {
        margin: ${MAP_FLOATING_CONTROL_INSET}px ${MAP_FLOATING_CONTROL_INSET}px 0 0;
      }
      /* The full-map route owns a 52pt re-frame button above this stack. */
      .mapboxgl-ctrl-top-right {
        top: calc(env(safe-area-inset-top, 0px) + 72px);
      }
      .mapboxgl-ctrl-group {
        background: ${colors.surfaceRaised};
        border: 1px solid ${colors.border};
        border-radius: ${RADIUS.md}px;
        box-shadow: none;
        overflow: hidden;
      }
      .mapboxgl-ctrl-group button {
        width: 44px;
        height: 44px;
        background-color: transparent;
      }
      .mapboxgl-ctrl-group button + button {
        border-top: 1px solid ${colors.border};
      }
      .mapboxgl-ctrl-group button .mapboxgl-ctrl-icon {
        background-size: 20px 20px;
        /* The icons are baked-in black SVG data URIs; inverting is the only
           way to get a light glyph without shipping our own sprites. */
        filter: ${isDark ? 'invert(1)' : 'none'};
      }
    `}</style>
  )
}

function PricePin({
  label,
  accessibilityLabel,
  onActivate,
}: {
  label: string
  accessibilityLabel: string
  onActivate: () => void
}) {
  return (
    <button
      type="button"
      className="tago-map-pin-button"
      data-testid="map.vehicle-marker"
      aria-label={accessibilityLabel}
      onPointerDown={stopMarkerDomEvent}
      onMouseDown={stopMarkerDomEvent}
      onTouchStart={stopMarkerDomEvent}
      onClick={(event) => {
        stopMarkerDomEvent(event)
        onActivate()
      }}
      onKeyDown={(event) => handleMarkerKeyPress(event, onActivate)}
    >
      <span
        className="tago-map-price-pill"
        style={{
          display: 'block',
          backgroundColor: COLORS.white,
          color: COLORS.coral.dark,
          fontFamily: FONTS.bodyBold,
          fontSize: 12,
          padding: '5px 11px',
          borderRadius: 999,
          border: `2px solid ${COLORS.coral.DEFAULT}`,
          whiteSpace: 'nowrap' as const,
          boxShadow: MAP_PILL_SHADOW,
          lineHeight: '16px',
        }}
      >
        {label}
      </span>
    </button>
  )
}

function ClusterPin({
  label,
  accessibilityLabel,
  onActivate,
}: {
  label: string
  accessibilityLabel: string
  onActivate: () => void
}) {
  return (
    <button
      type="button"
      className="tago-map-pin-button"
      data-testid="map.vehicle-cluster"
      aria-label={accessibilityLabel}
      onPointerDown={stopMarkerDomEvent}
      onMouseDown={stopMarkerDomEvent}
      onTouchStart={stopMarkerDomEvent}
      onClick={(event) => {
        stopMarkerDomEvent(event)
        onActivate()
      }}
      onKeyDown={(event) => handleMarkerKeyPress(event, onActivate)}
    >
      <span
        className="tago-map-cluster-pill"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box',
          minWidth: 32,
          height: 32,
          backgroundColor: COLORS.purple.DEFAULT,
          color: COLORS.white,
          fontFamily: FONTS.bodyBold,
          fontSize: 12,
          padding: '0 7px',
          borderRadius: 999,
          border: `2px solid ${COLORS.white}`,
          whiteSpace: 'nowrap' as const,
          boxShadow: MAP_PILL_SHADOW,
          lineHeight: '16px',
        }}
      >
        {label}
      </span>
    </button>
  )
}

/**
 * mapbox sets role=button, tabindex and aria-label on its marker elements.
 * That is right for a price pin and wrong for the user's own position, which
 * nothing can do anything with. React never owns those attributes, so they have
 * to be cleared from the DOM after the marker mounts.
 */
function useHideDecorativeMarker(epoch: number) {
  useEffect(() => {
    if (typeof document === 'undefined') return
    // Keyed off every mapbox marker rather than our own className: the class
    // react-map-gl forwards does not land on the element mapbox labels, which
    // the first version of this assumed and which measured as no change at all.
    //
    // The test is behavioural — a marker that contains no button is a
    // decoration. That covers the user-location dot without having to name it,
    // and cannot go stale if another decorative marker is added later.
    document.querySelectorAll('.mapboxgl-marker').forEach((el) => {
      if (el.querySelector('button')) return
      el.removeAttribute('role')
      el.removeAttribute('aria-label')
      el.setAttribute('aria-hidden', 'true')
      el.setAttribute('tabindex', '-1')
    })
  }, [epoch])
}

// ---- User Location Dot ----

function UserDot() {
  return (
    <div
      style={{
        position: 'relative',
        width: 24,
        height: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          position: 'absolute',
          width: 24,
          height: 24,
          borderRadius: 12,
          backgroundColor: `${COLORS.purple.DEFAULT}25`,
          animation: 'tago-pulse 2s ease-in-out infinite',
        }}
      />
      <div
        style={{
          width: 14,
          height: 14,
          borderRadius: 7,
          backgroundColor: COLORS.purple.DEFAULT,
          border: '2.5px solid #FFFFFF',
          boxShadow: '0px 1px 4px rgba(0,0,0,0.25)',
          position: 'relative',
          zIndex: 1,
        }}
      />
      <style>{`
        @keyframes tago-pulse {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(1.6); opacity: 0; }
        }
      `}</style>
    </div>
  )
}

// ---- Fallback (no token, or the map could not be loaded) ----

function MapFallback({
  height,
  isDark,
  colors,
  onRetry,
}: {
  height: number | '100%'
  isDark: boolean
  colors: ReturnType<typeof useSemanticColors>
  /** Only passed when the map failed at runtime — a missing key is not retryable. */
  onRetry?: () => void
}) {
  const { t } = useTranslation()
  return (
    <View
      style={{
        height,
        borderRadius: RADIUS.lg,
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: isDark ? colors.surface : '#F7F5FA',
        borderWidth: 1.5,
        borderColor: colors.border,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <LinearGradient
        pointerEvents="none"
        colors={
          isDark
            ? ['rgba(91,84,144,0.26)', 'rgba(239,78,74,0.10)']
            : ['rgba(91,84,144,0.10)', 'rgba(239,78,74,0.08)']
        }
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}
      />
      {[0, 1, 2, 3].map((i) => (
        <View
          key={i}
          pointerEvents="none"
          style={{
            position: 'absolute',
            left: `${-12 + i * 30}%` as unknown as number,
            top: `${10 + i * 18}%` as unknown as number,
            width: '130%' as unknown as number,
            height: 1,
            backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(91,84,144,0.10)',
            transform: [{ rotate: i % 2 === 0 ? '-12deg' : '9deg' }],
          }}
        />
      ))}
      <View
        pointerEvents="none"
        style={{
          position: 'absolute',
          top: '18%' as unknown as number,
          right: '18%' as unknown as number,
          width: 10,
          height: 10,
          borderRadius: 5,
          backgroundColor: COLORS.coral.dark,
          borderWidth: 2,
          borderColor: COLORS.white,
          ...webShadow({ y: 2, blur: 6, opacity: 0.16, elevation: 3 }),
        }}
      />
      <View
        pointerEvents="none"
        style={{
          position: 'absolute',
          bottom: '22%' as unknown as number,
          left: '24%' as unknown as number,
          width: 12,
          height: 12,
          borderRadius: 6,
          backgroundColor: COLORS.purple.DEFAULT,
          borderWidth: 2,
          borderColor: COLORS.white,
          ...webShadow({ y: 2, blur: 6, opacity: 0.16, elevation: 3 }),
        }}
      />
      <View
        style={{
          width: 64,
          height: 64,
          borderRadius: 20,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.78)',
          borderWidth: 1,
          borderColor: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.80)',
          ...webShadow({ y: 8, blur: 24, opacity: isDark ? 0.28 : 0.12, elevation: 8 }),
        }}
      >
        <MapIcon size={30} color={COLORS.purple.DEFAULT} strokeWidth={1.7} />
      </View>
      <Text
        style={{
          fontSize: 15,
          lineHeight: 21,
          fontFamily: FONTS.bodyBold,
          color: colors.textPrimary,
          marginTop: SPACING.md,
        }}
      >
        {t('explore.map_unavailable')}
      </Text>
      <Text
        style={{
          fontSize: 13,
          lineHeight: 18,
          fontFamily: FONTS.body,
          color: colors.textSecondary,
          textAlign: 'center',
          maxWidth: 300,
          marginTop: SPACING.xs,
          paddingHorizontal: SPACING.lg,
        }}
      >
        {t('explore.map_unavailable_desc')}
      </Text>
      {onRetry ? (
        <View style={{ marginTop: SPACING.md }}>
          <Button
            title={t('common.retry')}
            onPress={onRetry}
            variant="secondary"
            size="sm"
            testID="explore.map-retry"
          />
        </View>
      ) : null}
    </View>
  )
}

// ---- Camera framing ----

/**
 * Framing insets for the compact preview, in CSS px.
 *
 * A flat 80 on every side is two thirds of a 240pt card: it squeezed every
 * marker into an ~80px band down the middle, which is the reason three pills
 * ended up touching before a single collision rule ran. The card is not
 * symmetric, so its framing should not be either — a pill hangs *above* its
 * anchor, so only the top has to clear a pill's own height plus the floating
 * chips, while the bottom needs barely more than the card's own edge. That
 * turns the usable band from ~80px into ~126px on the same card.
 */
const MAP_FIT_INSET_TOP =
  MAP_OVERLAY_INSET + MAP_OVERLAY_CHIP_HEIGHT + MAP_PILL_HEIGHT + MAP_PILL_GAP
const MAP_FIT_INSET_BOTTOM = 24
const MAP_FIT_INSET_SIDE = 48
const MAP_FIT_INSET_DEFAULT = 80
/** fitBounds cannot honour padding that eats the canvas, so on anything too
 *  small for the full inset the ratio is kept and the whole set shrinks. */
const MAP_FIT_INSET_MAX_SHARE = 0.7

function compactFitPadding(map: ReturnType<MapRef['getMap']>) {
  const container = map.getContainer()
  const width = container?.clientWidth ?? 0
  const height = container?.clientHeight ?? 0
  const vertical = MAP_FIT_INSET_TOP + MAP_FIT_INSET_BOTTOM
  const horizontal = MAP_FIT_INSET_SIDE * 2
  const verticalScale =
    height > 0 && vertical > height * MAP_FIT_INSET_MAX_SHARE
      ? (height * MAP_FIT_INSET_MAX_SHARE) / vertical
      : 1
  const horizontalScale =
    width > 0 && horizontal > width * MAP_FIT_INSET_MAX_SHARE
      ? (width * MAP_FIT_INSET_MAX_SHARE) / horizontal
      : 1

  return {
    top: Math.round(MAP_FIT_INSET_TOP * verticalScale),
    bottom: Math.round(MAP_FIT_INSET_BOTTOM * verticalScale),
    left: Math.round(MAP_FIT_INSET_SIDE * horizontalScale),
    right: Math.round(MAP_FIT_INSET_SIDE * horizontalScale),
  }
}

function fitToMarkers(
  map: ReturnType<MapRef['getMap']>,
  clusters: VehicleMapCluster[],
  compact: boolean,
  preferredBounds?: MapboxCameraBounds | null,
) {
  if (preferredBounds) {
    map.fitBounds([preferredBounds.sw, preferredBounds.ne], {
      padding: compact
        ? compactFitPadding(map)
        : {
            top: MAP_FIT_INSET_DEFAULT,
            bottom: MAP_FIT_INSET_DEFAULT,
            left: MAP_FIT_INSET_SIDE,
            right: MAP_FIT_INSET_SIDE,
          },
      maxZoom: 15,
      duration: 0,
    })
    return
  }

  let minLng = Infinity
  let maxLng = -Infinity
  let minLat = Infinity
  let maxLat = -Infinity
  const extend = (lng: number, lat: number) => {
    if (lng < minLng) minLng = lng
    if (lng > maxLng) maxLng = lng
    if (lat < minLat) minLat = lat
    if (lat > maxLat) maxLat = lat
  }
  clusters.forEach((cluster) => {
    extend(cluster.coordinate.lng, cluster.coordinate.lat)
  })
  if (!Number.isFinite(minLng)) return
  if (minLng === maxLng && minLat === maxLat) {
    map.jumpTo({ center: [minLng, minLat], zoom: 14 })
    return
  }
  map.fitBounds(
    [
      [minLng, minLat],
      [maxLng, maxLat],
    ],
    {
      padding: compact
        ? compactFitPadding(map)
        : {
            top: MAP_FIT_INSET_DEFAULT,
            bottom: MAP_FIT_INSET_DEFAULT,
            left: MAP_FIT_INSET_SIDE,
            right: MAP_FIT_INSET_SIDE,
          },
      maxZoom: 15,
      duration: 0,
    },
  )
}

// ---- Main Component ----

export function MapPreview({
  vehicles,
  userLocation,
  onViewMap,
  onVehiclePress,
  desktop,
  height,
  hideCountBadge,
  compactPreview,
  initialFrame = 'results',
  fitRequest = 0,
  mapChrome,
}: MapPreviewProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { t } = useTranslation()
  const mapRef = useRef<MapRef>(null)
  const hostRef = useRef<View>(null)
  useLockPageScrollOverMap(hostRef)
  // Only the strings mapbox can actually surface here: attribution,
  // fullscreen, geolocate and scale controls are all off.
  const mapLocale = useMemo(
    () => ({
      'Map.Title': t('a11y.map_canvas'),
      'Marker.Title': t('a11y.map_marker'),
      'NavigationControl.ZoomIn': t('a11y.map_zoom_in'),
      'NavigationControl.ZoomOut': t('a11y.map_zoom_out'),
    }),
    [t],
  )
  const [mapFailed, setMapFailed] = useState(false)
  // Bumped once the mapbox instance exists, so the resize observer below
  // attaches to a real container rather than to nothing.
  const [mapLoadEpoch, setMapLoadEpoch] = useState(0)
  const mapReadyRef = useRef(false)
  const mapH = desktop ? undefined : (height ?? DEFAULT_MOBILE_MAP_PREVIEW_HEIGHT)
  const hasInitialFitRef = useRef(false)
  const appliedFitKeyRef = useRef<string | null>(null)
  const defaultDepartmentFrame =
    compactPreview && initialFrame === 'default-department'
      ? MAPBOX_DEFAULT_SV_DEPARTMENT_FRAME
      : null
  const handleViewMapPress = usePressAction(onViewMap)

  /**
   * One explicit zoom step for the compact preview, clamped to the same range
   * the native card uses so the two platforms cannot drift apart. `easeTo`
   * animates; a bare `zoomTo` would jump.
   */
  const handleWebZoomStep = useCallback((direction: 1 | -1) => {
    const map = mapRef.current?.getMap()
    if (!map) return
    const from = map.getZoom()
    const next = Math.min(MAP_ZOOM_MAX, Math.max(MAP_ZOOM_MIN, from + direction * MAP_ZOOM_STEP))
    if (next === from) return
    map.easeTo({ zoom: next, duration: MAP_ZOOM_ANIMATION_MS })
  }, [])
  const handleVehicleMarkerPress = usePressAction((vehicleId: string) =>
    onVehiclePress?.(vehicleId),
  )

  const center = useMemo(
    () => (userLocation ? { lng: userLocation.lng, lat: userLocation.lat } : DEFAULT_CENTER),
    [userLocation],
  )

  // One immutable geographic index per inventory response. At a fixed zoom its
  // marker IDs, membership and counts are independent of the viewport centre.
  const clusterIndex = useMemo(() => buildVehicleMapClusterIndex(vehicles), [vehicles])
  const vehicleClusters = clusterIndex.locations
  const locatedVehicleCount = useMemo(() => countLocatedVehicles(vehicles), [vehicles])
  const [clusterZoom, setClusterZoom] = useState<number | null>(null)
  useHideDecorativeMarker(mapLoadEpoch + (clusterZoom ?? 0))

  /** The cars behind the pill the user tapped. Empty means the sheet is closed. */
  const [clusterSheetVehicles, setClusterSheetVehicles] = useState<NearbyVehicle[]>([])
  const closeClusterSheet = useCallback(() => setClusterSheetVehicles([]), [])
  const handleClusterSheetSelect = useCallback(
    (vehicleId: string) => {
      setClusterSheetVehicles([])
      onVehiclePress?.(vehicleId)
    },
    [onVehiclePress],
  )
  /**
   * Keep web visually and semantically identical to native: a bare number in
   * a purple group pill is ambiguous next to price pills, while the localized
   * vehicle noun remains compact in the stretchable marker chrome.
   */
  const clusterPillLabel = useCallback(
    (grouped: NearbyVehicle[]) => {
      const first = grouped[0]
      if (grouped.length === 1 && first) {
        const sticker = getVehicleDisplayPriceCents(first)
        return formatPrice(sticker.cents, { unit: sticker.unit })
      }
      return t('explore.map_cluster_count', { count: grouped.length })
    },
    [t],
  )
  const placedClusterPills = useMemo<RenderedVehicleMapMarker[]>(() => {
    if (clusterZoom === null) return []
    return clusterIndex.markersAtZoom(clusterZoom).map((marker) => {
      const label = clusterPillLabel(marker.vehicles)
      const first = marker.vehicles[0]
      return {
        ...marker,
        label,
        accessibilityLabel:
          marker.vehicles.length === 1 && first
            ? t('explore.map_vehicle_marker_label', { vehicle: first.title, price: label })
            : t('explore.map_cluster_choose_label', { count: marker.vehicles.length }),
      }
    })
  }, [clusterIndex, clusterPillLabel, clusterZoom, t])

  const activateMapMarker = useCallback(
    (marker: RenderedVehicleMapMarker) => {
      if (compactPreview) {
        handleViewMapPress()
        return
      }

      const first = marker.vehicles[0]
      if (marker.vehicles.length === 1 && first) {
        handleVehicleMarkerPress(first.id)
        return
      }
      setClusterSheetVehicles(marker.vehicles)
    },
    [compactPreview, handleVehicleMarkerPress, handleViewMapPress],
  )

  useEffect(() => {
    setMapFailed(false)
    // The Standard light preset follows the appearance, like the native import.
    const map = mapRef.current?.getMap()
    if (map && mapReadyRef.current) applyMapboxWebConfig(map, 'discovery', isDark)
  }, [isDark])

  // Fit camera to show all markers when the map loads.
  const handleLoad = useCallback(() => {
    const map = mapRef.current?.getMap()
    if (!map) return

    mapReadyRef.current = true
    // La MISMA configuracion de Standard que el nativo monta como
    // <StyleImport id="basemap" existing>. Una sola fuente de verdad.
    applyMapboxWebConfig(map, 'discovery', isDark)
    // Before framing anything: mapbox sized its canvas from whatever the
    // container measured at mount, which inside an RN Web flex tree is often
    // before the row has settled. Fitting against a stale width put the
    // camera in the wrong place too. The fit itself runs from the effect
    // below, once the style can honour it.
    map.resize()
    setMapLoadEpoch((n) => n + 1)
    setClusterZoom(normalizeMapClusterZoom(map.getZoom()))
  }, [isDark])

  /**
   * Keep the canvas the same size as the card it lives in.
   *
   * mapbox only sizes its canvas when it is told to. In an RN Web tree the
   * container is measured at mount, before flex has resolved the row, so the
   * canvas can come out narrower than the card — which is what the light shots
   * show: a hard vertical seam with flat unpainted grey to its left on the
   * full-screen map, and a canvas of near-zero width in the preview card, i.e.
   * an empty grey rectangle with the overlays still floating over it. Nothing
   * theme-specific is involved; the dark shots simply won the race. Observing
   * the container removes the race entirely.
   */
  useEffect(() => {
    const map = mapRef.current?.getMap()
    const container = map?.getContainer()
    if (!map || !container) return

    map.resize()
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(() => map.resize())
    observer.observe(container)
    return () => observer.disconnect()
  }, [mapLoadEpoch])

  const handleMapError = useCallback((event: { error?: unknown }) => {
    const message = mapErrorMessage(event.error ?? event)

    // Once the base style is up, the map is usable. Mapbox fires `error` for
    // every dropped tile, sprite or glyph request, and tearing the whole map
    // down for one of them left the user staring at a static illustration for
    // the rest of the session — the tile would have retried on the next pan.
    // So after first load, classify and log, but keep the map on screen.
    const map = mapRef.current?.getMap()
    if (mapReadyRef.current || Boolean(map?.isStyleLoaded())) {
      if (__DEV__) {
        console.warn(
          MAP_NETWORK_ERROR.test(message)
            ? '[MapPreview] transient map provider error, keeping map mounted'
            : '[MapPreview] non-fatal map error, keeping map mounted',
          message,
        )
      }
      return
    }

    // Nothing ever rendered: the style itself never arrived. Show the fallback
    // — with a retry, because this is usually a one-off network failure.
    setMapFailed(true)
  }, [])

  const handleRetryMap = useCallback(() => {
    mapReadyRef.current = false
    hasInitialFitRef.current = false
    appliedFitKeyRef.current = null
    setClusterZoom(null)
    setMapFailed(false)
  }, [])

  /**
   * A drag is not a decision. The compact preview is pannable again (the owner
   * confirmed a direct drag must move the map on the home screen), and mapbox
   * fires `click` after the pointer is released even when it just dragged —
   * without this guard, moving the map would also open the full map.
   */
  const suppressMapClickRef = useRef(false)
  const suppressMapClickAfterGesture = useCallback(() => {
    suppressMapClickRef.current = true
  }, [])
  const handleMapClick = useCallback(() => {
    if (suppressMapClickRef.current) {
      suppressMapClickRef.current = false
      return
    }
    if (compactPreview) handleViewMapPress()
  }, [compactPreview, handleViewMapPress])

  // Frame the camera when the style can honour it.
  //
  // This used to `return` when the style was not loaded yet and never come
  // back, which is why the full map could open at its default zoom 13 with
  // vehicles outside the canvas: the data landed in the window between mount
  // and `load`, the guard bailed, and nothing re-ran it. Now the bail arms a
  // one-shot `idle` listener instead, so the fit happens on the first frame
  // that can honour it.
  //
  // The compact preview also re-fits when the inventory itself changes: after
  // a filter or a search the new markers would sit outside the canvas, and the
  // preview is too small for the person to find them by hand. The full map
  // fits once and never pulls the camera out from under someone exploring it.
  useEffect(() => {
    const map = mapRef.current?.getMap()
    if (!map) return
    if (vehicleClusters.length === 0 && !defaultDepartmentFrame) return

    const fitKey = compactPreview
      ? defaultDepartmentFrame
        ? 'default-department'
        : vehicleClusters
            .map((cluster) => cluster.id)
            .sort()
            .join('|')
      : 'initial'
    if (appliedFitKeyRef.current === fitKey) return
    if (!compactPreview && hasInitialFitRef.current) return

    const applyFit = () => {
      if (appliedFitKeyRef.current === fitKey) return
      if (!compactPreview && hasInitialFitRef.current) return
      if (__DEV__) {
        console.log('[MapPreview] fitting', vehicleClusters.length, 'locations')
      }
      fitToMarkers(map, vehicleClusters, Boolean(compactPreview), defaultDepartmentFrame)
      appliedFitKeyRef.current = fitKey
      hasInitialFitRef.current = true
      setClusterZoom(normalizeMapClusterZoom(map.getZoom()))
    }

    if (map.isStyleLoaded()) {
      applyFit()
      return
    }

    map.once('idle', applyFit)
    return () => {
      map.off('idle', applyFit)
    }
  }, [vehicleClusters, compactPreview, defaultDepartmentFrame, mapLoadEpoch])

  const appliedFitRequest = useRef(0)
  useEffect(() => {
    const map = mapRef.current?.getMap()
    if (!map || !mapReadyRef.current || vehicleClusters.length === 0) return
    if (fitRequest === appliedFitRequest.current) return
    fitToMarkers(map, vehicleClusters, Boolean(compactPreview))
    appliedFitRequest.current = fitRequest
  }, [fitRequest, vehicleClusters, compactPreview, mapLoadEpoch])

  if (!hasMapboxWebToken() || mapFailed) {
    return (
      <MapFallback
        height={desktop ? '100%' : (mapH ?? 320)}
        isDark={isDark}
        colors={colors}
        onRetry={hasMapboxWebToken() ? handleRetryMap : undefined}
      />
    )
  }

  return (
    <View
      ref={hostRef}
      style={{
        height: desktop ? undefined : mapH,
        flex: desktop ? 1 : undefined,
        minHeight: desktop ? 280 : mapH,
        borderRadius: desktop ? 0 : RADIUS.lg,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <MapMarkerInteractionStyles colors={colors} isDark={isDark} />
      <Map
        ref={mapRef}
        initialViewState={{
          longitude: center.lng,
          latitude: center.lat,
          zoom: compactPreview ? 13 : MAPBOX_DISCOVERY_HERO_ZOOM,
        }}
        style={{
          width: '100%',
          height: '100%',
          cursor: 'grab',
          touchAction: 'none',
          overscrollBehavior: 'contain',
        }}
        mapboxAccessToken={MAPBOX_WEB_TOKEN}
        mapStyle={MAPBOX_WEB_STYLE_URL}
        attributionControl={false}
        // La MISMA camara que las apps: el mapa completo entra en 3D dentro de
        // la ciudad; la tarjeta compacta se queda plana.
        pitch={compactPreview ? 0 : MAPBOX_DISCOVERY_PITCH}
        bearing={compactPreview ? 0 : MAPBOX_DISCOVERY_BEARING}
        // mapbox labels its own chrome in English. Without this, a Spanish
        // session announced the zoom buttons as "Zoom in" / "Zoom out" and
        // wrapped every price pill in a div labelled "Map marker" — untranslated
        // strings a screen reader reads out over the translated ones inside.
        locale={mapLocale}
        pitchWithRotate={false}
        dragRotate={false}
        dragPan
        touchZoomRotate
        touchPitch={false}
        // Wheel over the map zooms toward the cursor. The host lock on this
        // View stops the page from scrolling away while the pointer is here.
        scrollZoom={WEB_MAP_SCROLL_ZOOM}
        doubleClickZoom={!compactPreview}
        maxZoom={18}
        minZoom={8}
        onLoad={handleLoad}
        onZoomEnd={(event) => setClusterZoom(normalizeMapClusterZoom(event.viewState.zoom))}
        onError={handleMapError}
        onDragStart={suppressMapClickAfterGesture}
        onDragEnd={suppressMapClickAfterGesture}
        onClick={handleMapClick}
      >
        {placedClusterPills.map((pill) => {
          const isSingleVehicle = pill.vehicles.length === 1
          const vehicle = pill.vehicles[0]
          if (!vehicle) return null

          const activate = () => activateMapMarker(pill)

          return (
            <Marker
              key={pill.id}
              longitude={pill.coordinate.lng}
              latitude={pill.coordinate.lat}
              anchor="bottom"
              className={isSingleVehicle ? 'tago-map-vehicle-marker' : 'tago-map-cluster-marker'}
              style={{
                zIndex: Math.min(pill.vehicles.length, MAP_PILL_MAX_Z_INDEX),
                pointerEvents: 'auto',
              }}
            >
              {isSingleVehicle ? (
                <PricePin
                  label={pill.label}
                  accessibilityLabel={pill.accessibilityLabel}
                  onActivate={activate}
                />
              ) : (
                <ClusterPin
                  label={pill.label}
                  accessibilityLabel={pill.accessibilityLabel}
                  onActivate={activate}
                />
              )}
            </Marker>
          )
        })}

        {/* User location pulse dot.
            mapbox gives every Marker a button role and its own aria-label, so
            this decorative dot announced itself as "Marcador del mapa" and was
            measured as a 24x24 control on eleven routes. It is where you are,
            not something you press — `useHideDecorativeMarker` strips the role
            after mount. */}
        {userLocation && (
          <Marker
            longitude={userLocation.lng}
            latitude={userLocation.lat}
            anchor="center"
            className="tago-map-user-dot"
          >
            <UserDot />
          </Marker>
        )}

        {/* Zoom affordance — only on the full-screen map, where zooming is a
            primary action. The previews stay chrome-free. */}
        {hideCountBadge ? <NavigationControl position="top-right" showCompass={false} /> : null}
      </Map>

      {/* Overlay: vehicle count badge. Suppressed when the parent screen
          is the full-screen map — that screen has its own floating
          BackButton at top-left and the badge would render underneath. */}
      {hideCountBadge ? null : (
        <GlassSurface
          tone="regular"
          visualRole="chrome"
          radius={RADIUS.md}
          pointerEvents="none"
          style={{
            position: 'absolute',
            top: MAP_OVERLAY_INSET,
            left: MAP_OVERLAY_INSET,
            zIndex: 10,
            height: MAP_OVERLAY_CHIP_HEIGHT,
          }}
          contentStyle={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            paddingHorizontal: 14,
          }}
        >
          <Text
            style={{
              fontSize: 12,
              fontFamily: FONTS.body,
              color: colors.textPrimary,
            }}
          >
            {t('explore.vehicles_on_map', { count: locatedVehicleCount })}
          </Text>
        </GlassSurface>
      )}

      {/* Overlay: "Ver mapa" button */}
      {hideCountBadge ? null : (
        <Pressable
          onPress={handleViewMapPress}
          accessibilityRole="button"
          accessibilityLabel={t('explore.view_map')}
          testID="explore.view-map"
          style={({ pressed }) => ({
            position: 'absolute',
            top: MAP_OVERLAY_INSET,
            right: 12,
            zIndex: 10,
            backgroundColor: COLORS.coral.dark,
            paddingHorizontal: 14,
            paddingVertical: 8,
            minHeight: 44,
            minWidth: 44,
            borderRadius: RADIUS.md,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 5,
            borderWidth: 1,
            borderColor: COLORS.coral.DEFAULT,
            opacity: pressed ? 0.9 : 1,
            ...webShadow({ y: 2, blur: 4, opacity: 0.1, elevation: 3 }),
          })}
        >
          <MapIcon size={14} color="#FFFFFF" strokeWidth={2} />
          <Text style={{ fontSize: 12, fontFamily: FONTS.body, color: '#FFFFFF' }}>
            {t('explore.view_map')}
          </Text>
        </Pressable>
      )}

      {/* Compact preview zoom — the same three-control row the native card
          renders: count chip, "Ver mapa", and this pill, all at 44dp. The
          full-screen route keeps Mapbox's own NavigationControl instead. */}
      {hideCountBadge ? null : (
        <GlassSurface
          tone="regular"
          visualRole="chrome"
          radius={RADIUS.md}
          style={{
            position: 'absolute',
            right: MAP_OVERLAY_INSET,
            bottom: MAP_OVERLAY_INSET,
            zIndex: 10,
            flexDirection: 'row',
            alignItems: 'center',
            height: MAP_OVERLAY_CHIP_HEIGHT,
          }}
        >
          <Pressable
            onPress={() => handleWebZoomStep(-1)}
            accessibilityRole="button"
            accessibilityLabel={t('explore.zoom_out')}
            testID="explore.map-zoom-out"
            style={({ pressed }) => ({
              width: 44,
              height: 44,
              alignItems: 'center',
              justifyContent: 'center',
              opacity: pressed ? 0.55 : 1,
            })}
          >
            <Minus size={18} color={colors.textPrimary} strokeWidth={2.4} />
          </Pressable>
          <View
            pointerEvents="none"
            style={{ width: 1, height: SPACING.lg, backgroundColor: colors.border }}
          />
          <Pressable
            onPress={() => handleWebZoomStep(1)}
            accessibilityRole="button"
            accessibilityLabel={t('explore.zoom_in')}
            testID="explore.map-zoom-in"
            style={({ pressed }) => ({
              width: 44,
              height: 44,
              alignItems: 'center',
              justifyContent: 'center',
              opacity: pressed ? 0.55 : 1,
            })}
          >
            <Plus size={18} color={colors.textPrimary} strokeWidth={2.4} />
          </Pressable>
        </GlassSurface>
      )}

      {/* Full-screen route controls deliberately share the map stack on both
          platforms. The web renderer used to drop this prop entirely, leaving
          a full-screen map without its back, fit, or result actions. */}
      {mapChrome}

      {/* The way into a pile. Mounted here rather than in each screen so the
          full-screen map route and the desktop explore pane get it from one
          place. */}
      <MapClusterSheet
        vehicles={clusterSheetVehicles}
        onSelect={handleClusterSheetSelect}
        onClose={closeClusterSheet}
      />
    </View>
  )
}

function mapErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  if (error && typeof error === 'object') {
    const record = error as Record<string, unknown>
    const nested = record.error ?? record.message ?? record.statusText
    if (typeof nested === 'string') return nested
    if (nested instanceof Error) return nested.message
  }
  return String(error)
}
